import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import fleetApi from "@/lib/fleetApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Panel, PageHeader, StatusPill, Loading } from "@/components/portal/widgets";
import DriverFormDialog from "@/components/portal/DriverFormDialog";
import { DriverOnboardingPanel } from "@/components/portal/DriverOnboardingPanel";
import VehicleFormDialog from "@/components/portal/VehicleFormDialog";
import VehicleAssignDialog from "@/components/portal/VehicleAssignDialog";
import VehicleHistoryDialog from "@/components/portal/VehicleHistoryDialog";
import { Star, Plus, Pencil, History } from "lucide-react";

const TABS = [["drivers", "Drivers"], ["vehicles", "Vehicles"]];
const EDIT_DRIVER_ROLES = ["owner", "supervisor", "dispatcher"];
const EDIT_VEHICLE_ROLES = ["owner", "supervisor", "dispatcher", "fleet_manager"];

export default function FleetPage() {
  const navigate = useNavigate();
  const { user, entitlements } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "vehicles" ? "vehicles" : "drivers");
  const [drivers, setDrivers] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [driverForm, setDriverForm] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [vehicleForm, setVehicleForm] = useState(undefined);

  const canEditDriver = EDIT_DRIVER_ROLES.includes(user?.role);
  const canEditVehicle = EDIT_VEHICLE_ROLES.includes(user?.role);
  const canAssignVehicle = ["owner", "administrator"].includes(user?.role);
  const [assign, setAssign] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    Promise.all([fleetApi.get("/drivers"), fleetApi.get("/vehicles")])
      .then(([d, v]) => { setDrivers(d.data); setVehicles(v.data); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!drivers) return <Loading />;

  const limits = entitlements?.limits || {};
  const dLimit = limits.drivers != null ? ` / ${limits.drivers}` : "";
  const vLimit = limits.vehicles != null ? ` / ${limits.vehicles}` : "";
  const tryAdd = (type) => {
    if (type === "driver" && limits.drivers != null && drivers.length >= limits.drivers) {
      toast.error(`Your plan includes up to ${limits.drivers} drivers. Upgrade to add more.`);
      navigate("/portal/billing");
      return;
    }
    if (type === "vehicle" && limits.vehicles != null && vehicles.length >= limits.vehicles) {
      toast.error(`Your plan includes up to ${limits.vehicles} vehicles. Upgrade to add more.`);
      navigate("/portal/billing");
      return;
    }
    if (type === "driver") setDriverForm(null); else setVehicleForm(null);
  };

  return (
    <div data-testid="fleet-page">
      <PageHeader title="Drivers & Fleet" subtitle={`${drivers.length}${dLimit} drivers · ${vehicles.length}${vLimit} vehicles`}>
        {tab === "drivers" && canEditDriver && (
          <button onClick={() => tryAdd("driver")} data-testid="add-driver-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800">
            <Plus className="h-4 w-4" /> Add Driver
          </button>
        )}
        {tab === "vehicles" && canEditVehicle && (
          <button onClick={() => tryAdd("vehicle")} data-testid="add-vehicle-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800">
            <Plus className="h-4 w-4" /> Add Vehicle
          </button>
        )}
      </PageHeader>

      {(() => {
        const warns = [];
        if (limits.drivers != null && drivers.length >= 0.8 * limits.drivers && drivers.length < limits.drivers) warns.push(`drivers (${drivers.length}/${limits.drivers})`);
        if (limits.vehicles != null && vehicles.length >= 0.8 * limits.vehicles && vehicles.length < limits.vehicles) warns.push(`vehicles (${vehicles.length}/${limits.vehicles})`);
        return warns.length ? (
          <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="soft-limit-warning">
            <span>You're approaching your plan limit for {warns.join(" and ")}. Upgrade to avoid interruptions.</span>
            <button onClick={() => navigate("/portal/billing")} data-testid="soft-limit-upgrade"
              className="shrink-0 rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-slate-900 hover:bg-amber-400">Upgrade</button>
          </div>
        ) : null;
      })()}

      <div className="mb-5 flex gap-2 border-b border-slate-800">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`fleet-tab-${k}`}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "border-blue-500 text-blue-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{label}</button>
        ))}
      </div>

      {tab === "drivers" && canEditDriver && <DriverOnboardingPanel reloadKey={drivers.length} />}
      {tab === "drivers" && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="drivers-grid">
          {drivers.map((d) => (
            <div key={d.id} role="button" tabIndex={0}
              onClick={() => navigate(`/portal/drivers/${d.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/portal/drivers/${d.id}`); }}
              className="cursor-pointer rounded-xl border border-slate-800 bg-[#111827] p-5 text-left transition-colors hover:border-blue-600/60 hover:bg-[#141c2e] focus:outline-none focus:ring-2 focus:ring-blue-600" data-testid={`driver-card-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">{(d.full_name || d.name).split(" ").map((p) => p[0]).slice(0, 2).join("")}</span>
                  <div>
                    <div className="font-semibold text-white">{d.full_name || d.name}</div>
                    <div className="font-mono-data text-[11px] text-slate-500">{d.license_number}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill value={d.available ? d.status : "off_duty"} />
                  {canEditDriver && (
                    <button onClick={(e) => { e.stopPropagation(); setDriverForm(d); }} data-testid={`edit-driver-${d.id}`}
                      className="rounded border border-slate-700 bg-slate-800/60 p-1.5 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white">
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-800 pt-3 text-center text-xs">
                <div><div className="flex items-center justify-center gap-1 font-mono-data text-emerald-400"><Star className="h-3 w-3 fill-emerald-400" />{d.metrics?.on_time_rate ?? "—"}%</div><div className="text-slate-500">on-time</div></div>
                <div><div className="font-mono-data text-white">{d.metrics?.trips_completed ?? 0}</div><div className="text-slate-500">trips</div></div>
                <div><div className="font-mono-data text-amber-400">{d.metrics?.incidents ?? 0}</div><div className="text-slate-500">incidents</div></div>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">License exp: {d.license_expiration}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "vehicles" && (
        <Panel testid="vehicles-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Fleet</th><th className="px-3 py-3">Vehicle</th><th className="px-3 py-3">Plate</th><th className="px-3 py-3">Type</th><th className="px-3 py-3">Capacity</th><th className="px-3 py-3">Mileage</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Driver</th>
                <th className="px-3 py-3">Action</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-800">
                {vehicles.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-800/30" data-testid={`vehicle-row-${v.id}`}>
                    <td className="px-5 py-3 font-mono-data font-bold text-blue-400">{v.fleet_number}</td>
                    <td className="px-3 py-3 text-white">{v.year} {v.make} {v.model}</td>
                    <td className="px-3 py-3 font-mono-data text-slate-300">{v.plate}</td>
                    <td className="px-3 py-3"><StatusPill value={v.vehicle_type} />{v.wheelchair_accessible ? " ♿" : ""}</td>
                    <td className="px-3 py-3 font-mono-data text-slate-400">{v.capacity}</td>
                    <td className="px-3 py-3 font-mono-data text-slate-400">{v.mileage?.toLocaleString()} mi</td>
                    <td className="px-3 py-3"><StatusPill value={v.status} /></td>
                    <td className="px-3 py-3 text-slate-200" data-testid={`vehicle-driver-${v.id}`}>
                      {v.current_driver_id ? (drivers.find((d) => d.id === v.current_driver_id)?.full_name || v.current_driver_id) : <span className="text-slate-500">— free</span>}
                      {canAssignVehicle && (
                        <button onClick={() => setAssign({ vehicleId: v.id })} data-testid={`assign-vehicle-driver-${v.id}`}
                          className="ml-2 rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] font-medium text-blue-300 hover:bg-slate-700">
                          {v.current_driver_id ? "Change" : "Assign"}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1.5">
                        <button onClick={() => setHistory(v)} data-testid={`vehicle-history-${v.id}`}
                          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/60 px-2 py-1 text-[11px] font-medium text-slate-200 transition-colors hover:bg-slate-700">
                          <History className="h-3 w-3" /> History
                        </button>
                        {canEditVehicle && (
                          <button onClick={() => setVehicleForm(v)} data-testid={`edit-vehicle-${v.id}`}
                            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/60 px-2 py-1 text-[11px] font-medium text-blue-300 transition-colors hover:bg-slate-700">
                            <Pencil className="h-3 w-3" /> Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {history && <VehicleHistoryDialog vehicle={history} drivers={drivers} open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }} />}
      {assign && <VehicleAssignDialog vehicleId={assign.vehicleId} open={!!assign} onOpenChange={(o) => { if (!o) setAssign(null); }} onSaved={load} />}
      <DriverFormDialog driver={driverForm} open={driverForm !== undefined}
        onOpenChange={(o) => { if (!o) setDriverForm(undefined); }} onSaved={load} />
      <VehicleFormDialog vehicle={vehicleForm} open={vehicleForm !== undefined}
        onOpenChange={(o) => { if (!o) setVehicleForm(undefined); }} onSaved={load} />
    </div>
  );
}
